import { checkAndRequestNotificationPermissions, isAndroid, isWeb } from "@stepapp/core";
import { addSentryBreadcrumb } from "@stepapp/sentry";
import { CustomerIO } from "customerio-reactnative";
import * as Device from "expo-device";
import * as Notifications from "expo-notifications";
import { Href, useRouter } from "expo-router";
import { useCallback, useEffect, useRef } from "react";

import { PushNotificationsType } from "./types";

export function useRegisterPushNotifications() {
  const { navigate } = useRouter();

  const notificationListener = useRef<ReturnType<
    typeof Notifications.addNotificationReceivedListener
  > | null>(null);
  const responseListener = useRef<ReturnType<
    typeof Notifications.addNotificationResponseReceivedListener
  > | null>(null);

  const registerResponseListener = useCallback(() => {
    responseListener.current = Notifications.addNotificationResponseReceivedListener(
      ({ notification }) => {
        const url = notification.request.content.data?.url as Href;

        if (url) navigate(url);
      },
    );
  }, [navigate]);

  const registerNotificationListener = useCallback(() => {
    notificationListener.current = Notifications.addNotificationReceivedListener((notification) => {
      const { data } = notification.request.content;
      if (!data) return;

      switch (data.type as PushNotificationsType) {
        case PushNotificationsType.PAYMENT_FAILED: {
          navigate("/calendar");
          break;
        }
      }
    });
  }, [navigate]);

  const subscribe = useCallback(async () => {
    if (Device.isDevice) {
      const granted = await checkAndRequestNotificationPermissions();

      if (granted) {
        try {
          const token = await Notifications.getDevicePushTokenAsync();

          registerNotificationListener();
          registerResponseListener();

          if (typeof token?.data === "string") {
            CustomerIO.registerDeviceToken(token.data);
          }
        } catch (error) {
          addSentryBreadcrumb("Push notifications registration failed", {
            level: "info",
            data: error,
            category: "pushNotifications",
          });
        }
      }
    }
  }, [registerResponseListener, registerNotificationListener]);

  useEffect(() => {
    if (isAndroid) {
      Notifications.setNotificationChannelAsync("default", {
        name: "default",
        importance: Notifications.AndroidImportance.DEFAULT,
      });
    }

    subscribe();

    return () => {
      if (notificationListener.current) {
        Notifications.removeNotificationSubscription(notificationListener.current);
      }
      if (responseListener.current) {
        Notifications.removeNotificationSubscription(responseListener.current);
      }
    };
  }, [subscribe]);
}

export async function handleInitialNotification(navigate: (url: Href) => void) {
  if (isWeb) return;

  const initialNotification = await Notifications.getLastNotificationResponseAsync();
  if (!initialNotification) return;

  const notificationContent = initialNotification.notification.request.content;
  if (notificationContent?.data) {
    const { type } = notificationContent.data;

    if (type as PushNotificationsType) {
      switch (type) {
        case PushNotificationsType.PAYMENT_FAILED:
        case PushNotificationsType.WORKER_ASSIGNED:
        case PushNotificationsType.WORKER_IN_ROUTE:
        case PushNotificationsType.RESERVATION_WORK_STARTED:
        case PushNotificationsType.RESERVATION_WORK_FINISHED: {
          navigate("/calendar");
          break;
        }

        default:
          break;
      }
    }
  }
}
